'use client';

import React from 'react';
import { Avatar, Chip, Link } from '@heroui/react';
import { RevshareTransaction } from '../types';
import { getCampaignTypeColors } from '../constants';
import Typography from '../../../../components/typography/Typography';
import { useTranslations } from '../../../../contexts/translation.context';
import { getPlatformIcon, transformPlatform } from '../../../../utils/platformUtils';

interface CampaignCellProps {
  transaction: RevshareTransaction;
}

const CampaignCell: React.FC<CampaignCellProps> = ({ transaction }) => {
  const { model, company } = transaction;
  const { lang } = useTranslations();
  const typeColors = getCampaignTypeColors();
  // Determine campaign type - RevShare by default for revshare transactions
  const campaignType = transaction?.compaign_type === 'refshare' ? 'RevShare' : 'CPL';
  const typeStyle = typeColors[campaignType as keyof typeof typeColors] || typeColors.RevShare;

  // Format date from company created_at
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Extract campaign ID from link (e.g., "c144" from "https://onlyfans.com/mitsudark/c144")
  const getCampaignIdFromLink = (link?: string) => {
    if (!link) return null;
    const parts = link.split('/');
    return parts[parts.length - 1] || null;
  };

  const campaignId = getCampaignIdFromLink(company?.link);
  const username = model?.of_user_name || '';
  const avatar = model?.avatar || '';
  const source = company?.source || '';
  const date = formatDate(company?.created_at);

  return (
    <div className="flex items-center gap-4">
      <Avatar src={avatar} className="w-10 h-10 rounded-3" radius="lg" />
      <div className="flex flex-col gap-1">
        {/* Row 1: Campaign ID and username */}
        <div className="flex items-center gap-2">
          <Typography variant="label2" className="!lowercase !text-auth-ink">
            {campaignId?.slice(-4) || `Campaign ${company?.of_id || ''}`}
          </Typography>
          {username && (
            <Link
              href={`https://onlyfans.com/${username}`}
              target="_blank"
              className="text-partner text-sm font-normal"
            >
              @{username}
            </Link>
          )}
        </div>
        {/* Row 2: Type tag and source + date */}
        <div className="flex items-center gap-2">
          <Chip
            size="sm"
            classNames={{
              base: `${typeStyle.bg} h-6 px-2`,
              content: `${typeStyle.text} text-xs font-normal p-0`,
            }}
          >
            {campaignType}
          </Chip>
          <Chip
            size="sm"
            variant="bordered"
            startContent={<span className="flex items-center">{getPlatformIcon(source)}</span>}
            classNames={{
              base: 'h-6 px-2 border-[1.4px] border-zinc-300',
              content: 'text-auth-ink text-xs font-normal p-0 flex items-center gap-1',
            }}
          >
            {transformPlatform(source)} {date}
          </Chip>
        </div>
      </div>
    </div>
  );
};

export default CampaignCell;
